import { createElement } from 'react';
import { Box, FileText, Film, Image, Layers, Mic, Music, Type, Video } from 'lucide-react';
import { sanitizeAnnotationTargetLabel } from '@/lib/annotations/annotation-target';
import { extractAnnotationMentionUserIds } from '@/lib/annotations/annotation-mentions';
import type {
  AnnotationView,
  ProjectAnnotation,
  ProjectAnnotationStatus,
} from '@/types/project-annotations';
import type { ProjectMember } from '@/types';

export const ANNOTATION_STATUS_VALUES = [
  1, 2, 3, 4,
] as const satisfies readonly ProjectAnnotationStatus[];

const ANNOTATION_STATUS_LABELS: Record<AnnotationView, Record<ProjectAnnotationStatus, string>> = {
  sent: {
    1: '待反馈',
    2: '待验收',
    3: '已完成',
    4: '撤销',
  },
  received: {
    1: '待处理',
    2: '已修改',
    3: '已完成',
    4: '已关闭',
  },
};

const ANNOTATION_STATUS_ACTION_LABELS: Record<
  AnnotationView,
  Record<ProjectAnnotationStatus, string>
> = {
  sent: {
    1: '待反馈',
    2: '待验收',
    3: '完成',
    4: '撤销',
  },
  received: {
    1: '待处理',
    2: '标记已修改',
    3: '完成',
    4: '关闭',
  },
};

export const ANNOTATION_STATUS_OPTIONS = ANNOTATION_STATUS_VALUES.map((value) => ({
  value,
  label: ANNOTATION_STATUS_LABELS.received[value],
}));

export function getAnnotationStatusOptions(view: AnnotationView) {
  return ANNOTATION_STATUS_VALUES.map((value) => ({
    value,
    label: getAnnotationStatusLabel(value, view),
  }));
}

export function getAnnotationStatusActionOptions(view: AnnotationView) {
  return ANNOTATION_STATUS_VALUES.map((value) => ({
    value,
    label: getAnnotationStatusActionLabel(value, view),
  }));
}

export function resolveAnnotationViewForUser(
  annotation: Pick<ProjectAnnotation, 'authorUserId'>,
  currentUserId: string | null,
  fallbackView: AnnotationView
): AnnotationView {
  if (!currentUserId) return fallbackView;
  return annotation.authorUserId === currentUserId ? 'sent' : 'received';
}

export function getAvailableAnnotationStatusOptions(
  annotation: Pick<ProjectAnnotation, 'authorUserId' | 'status'>,
  currentUserId: string | null
): ProjectAnnotationStatus[] {
  if (annotation.status === 3 || annotation.status === 4) {
    return [annotation.status];
  }
  if (currentUserId && annotation.authorUserId === currentUserId) {
    return [1, 3, 4];
  }
  return [1, 2];
}

export function canReviewAnnotationAcceptance(
  annotation: Pick<ProjectAnnotation, 'authorUserId' | 'status'>,
  currentUserId: string | null,
  view: AnnotationView
): boolean {
  return Boolean(
    view === 'sent' &&
    currentUserId &&
    annotation.authorUserId === currentUserId &&
    annotation.status === 2
  );
}

export function splitAnnotationReplies(annotation: Pick<ProjectAnnotation, 'replies'>): {
  textReplies: ProjectAnnotation['replies'];
  statusChanges: ProjectAnnotation['replies'];
} {
  return {
    textReplies: annotation.replies.filter((reply) => reply.metadata?.kind !== 'status_change'),
    statusChanges: annotation.replies.filter((reply) => reply.metadata?.kind === 'status_change'),
  };
}

export function getMemberDisplayName(member: ProjectMember): string {
  return member.realName || member.nickname || member.username || member.phone || member.userId;
}

export function mergeProjectMembers(...memberLists: ProjectMember[][]): ProjectMember[] {
  const memberByUserId = new Map<string, ProjectMember>();
  for (const members of memberLists) {
    for (const member of members) {
      if (!memberByUserId.has(member.userId)) {
        memberByUserId.set(member.userId, member);
      }
    }
  }
  return Array.from(memberByUserId.values());
}

export function findMentionKeyword(value: string): string | null {
  const match = value.match(/(^|\s)@([^\s@]*)$/u);
  return match ? match[2] : null;
}

export function getInitialText(value?: string | null): string {
  return value?.trim().slice(0, 1) || '?';
}

function isProjectAnnotationStatus(value: number): value is ProjectAnnotationStatus {
  return ANNOTATION_STATUS_VALUES.includes(value as ProjectAnnotationStatus);
}

export function getAnnotationStatusLabel(value: number, view: AnnotationView): string {
  return isProjectAnnotationStatus(value) ? ANNOTATION_STATUS_LABELS[view][value] : '未知';
}

export function getAnnotationStatusActionLabel(value: number, view: AnnotationView): string {
  return isProjectAnnotationStatus(value) ? ANNOTATION_STATUS_ACTION_LABELS[view][value] : '未知';
}

export function getStatusLabel(value: number, view: AnnotationView): string {
  return getAnnotationStatusLabel(value, view);
}

export function getStatusStyles(status: number): string {
  return status === 4
    ? 'bg-transparent text-text-secondary border-transparent hover:bg-fill-default hover:text-text-primary focus:ring-border-dark/10'
    : 'bg-transparent text-text-regular border-transparent hover:bg-fill-default hover:text-text-primary focus:ring-border-dark/10';
}

export function getStatusDotColor(status: number): string {
  switch (status) {
    case 1:
      return 'bg-jt-bronze';
    case 2:
      return 'bg-jt-purple';
    case 3:
      return 'bg-jt-blue';
    case 4:
      return 'bg-jt-green';
    default:
      return 'bg-text-secondary';
  }
}

export function getTargetIcon(type?: string) {
  switch (type) {
    case 'storyboard':
      return createElement(Film, { className: 'h-3.5 w-3.5 text-jt-blue' });
    case 'storyboard_item':
      return createElement(Layers, { className: 'h-3.5 w-3.5 text-jt-green' });
    case 'storyboard_image':
      return createElement(Image, { className: 'h-3.5 w-3.5 text-jt-blue' });
    case 'video':
    case 'storyboard_video':
      return createElement(Video, { className: 'h-3.5 w-3.5 text-jt-purple' });
    case 'dialogue':
      return createElement(Type, { className: 'h-3.5 w-3.5 text-jt-carmine' });
    case 'voiceover':
      return createElement(Mic, { className: 'h-3.5 w-3.5 text-jt-bronze' });
    case 'sound_effect':
      return createElement(Music, { className: 'h-3.5 w-3.5 text-jt-green' });
    case 'resource_tag':
    case 'project_resource':
    case 'resource_element':
      return createElement(Box, { className: 'h-3.5 w-3.5 text-jt-green' });
    default:
      return createElement(FileText, { className: 'h-3.5 w-3.5 text-text-secondary' });
  }
}

export function readMetadataString(metadata: Record<string, unknown>, key: string): string | null {
  const value = metadata[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

export function readMetadataNumber(metadata: Record<string, unknown>, key: string): number | null {
  const value = metadata[key];
  return typeof value === 'number' ? value : null;
}

export function getAnnotationTargetLabel(annotation: ProjectAnnotation): string | null {
  if (annotation.targetType === 'none') {
    return null;
  }
  const rawLabel =
    annotation.metadata.annotationTarget?.label ??
    annotation.targetSummary ??
    readMetadataString(annotation.metadata, 'targetSummary');
  return sanitizeAnnotationTargetLabel(rawLabel);
}

export function getMentionToken(member: ProjectMember): string {
  return `@${getMemberDisplayName(member)}`;
}

export function getMentionedUserIds(value: string, members: ProjectMember[]): string[] {
  return extractAnnotationMentionUserIds(
    value,
    members.map((member) => ({
      userId: member.userId,
      displayName: getMemberDisplayName(member),
    }))
  );
}

export function getMentionOptions(
  members: ProjectMember[],
  keyword: string | null,
  selectedMembers: ProjectMember[],
  value: string
): ProjectMember[] {
  if (keyword === null) return [];

  const selectedIds = new Set(
    selectedMembers
      .filter((member) => value.includes(getMentionToken(member)))
      .map((member) => member.userId)
  );
  const normalizedKeyword = keyword.toLowerCase();
  return members
    .filter((member) => !selectedIds.has(member.userId))
    .filter((member) => {
      if (!normalizedKeyword) return true;
      return getMemberDisplayName(member).toLowerCase().includes(normalizedKeyword);
    })
    .slice(0, 6);
}

export type AnnotationSheetResizeDirection = 'up' | 'down';

export function resolveAnnotationSheetNextHeight(params: {
  startHeight: number;
  startY: number;
  clientY: number;
  minHeight: number;
  maxHeight: number;
}): number {
  const delta = params.startY - params.clientY;
  return Math.min(params.maxHeight, Math.max(params.minHeight, params.startHeight + delta));
}

const TARGET_ANNOTATION_SHEET_PANEL_BASE_CLASS =
  'relative flex max-h-full w-full flex-col overflow-visible rounded-2xl border border-border-light bg-bg-element/90 backdrop-blur-2xl pointer-events-auto';

export function resolveTargetAnnotationSheetChrome(params: {
  expandsDown: boolean;
  isClosing: boolean;
}): {
  panelClassName: string;
  showOverlay: boolean;
} {
  const { expandsDown, isClosing } = params;

  if (expandsDown) {
    return {
      panelClassName: `${TARGET_ANNOTATION_SHEET_PANEL_BASE_CLASS} max-w-none shadow-lg`,
      showOverlay: false,
    };
  }

  const motionClassName = isClosing
    ? 'pointer-events-none animate-out fade-out-0 duration-300 ease-in fill-mode-forwards slide-out-to-bottom-full'
    : 'animate-in fade-in duration-500 ease-out slide-in-from-bottom-full';

  return {
    panelClassName: `${TARGET_ANNOTATION_SHEET_PANEL_BASE_CLASS} max-w-[560px] shadow-[0_-20px_50px_rgba(0,0,0,0.35)] ${motionClassName}`,
    showOverlay: true,
  };
}
